import * as React from 'react'
import { FC, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { defaultFilters as defaultLeadFilters } from '@user/store/lead/filters'
import { defaultFilters as defaultOppFilters } from '@user/store/opportunity/filters'
import { ISalesFunnelCard } from '@user/store/salesFunnelSummary/typings'
import { ReasonsPopover } from './ReasonsPopover'
import { SalesFunnelMetric } from '../SalesFunnelMetric'
import { useNavigate } from 'react-router'
import { useSaveLeadFilters } from '@user/store/lead/hooks'
import { useSaveOpportunityFilters } from '@user/store/opportunity/hooks'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { selectSalesFunnelFilters } from '@user/store/salesFunnelSummary/slice'
import { Nullable } from '@user/components/Filters/typings'
import { useSoldServiceStatuses } from '../SalesFunnelSummary/hooks/useSoldServicesStatuses'
import { buildLostOpportunityFilters } from '../utils/buildLostOpportunityFilters'

interface Props {
  funnel: ISalesFunnelCard
  handleClose: () => void
}

export const SalesFunnelCardMetrics: FC<Props> = (props) => {
  const {
    funnel,
    funnel: { salesFunnelId, salesFunnelName },
    handleClose,
  } = props

  const navigate = useNavigate()
  const saveLeadFilters = useSaveLeadFilters()
  const saveOpportunityFilters = useSaveOpportunityFilters()
  const funnelFilters = useSelector(selectSalesFunnelFilters)
  const { data } = useSoldServiceStatuses()

  const goToLeads = useCallback(
    (isClosed: Nullable) => {
      saveLeadFilters({
        ...defaultLeadFilters,
        filters: {
          ...defaultLeadFilters.filters,
          createdDate:
            isClosed === 'notnull'
              ? defaultLeadFilters.filters.createdDate
              : {
                  ...defaultLeadFilters.filters.createdDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
          closed: {
            ...defaultLeadFilters.filters.closed,
            value: isClosed,
          },
          salesFunnelId: {
            ...defaultLeadFilters.filters.salesFunnelId,
            value:
              salesFunnelId && salesFunnelName
                ? {
                    mode: 'any',
                    options: [{ label: salesFunnelName, value: salesFunnelId }],
                  }
                : defaultOppFilters.filters.salesFunnelId.value,
          },
          assignee: {
            ...defaultLeadFilters.filters.assignee,
            value: funnelFilters.filters.ownerIds.value,
          },
          salesTeamIds: {
            ...defaultLeadFilters.filters.salesTeamId,
            value: funnelFilters.filters.salesTeamIds.value,
          },
          disqualifiedDate:
            isClosed === 'null'
              ? defaultLeadFilters.filters.disqualifiedDate
              : {
                  ...defaultLeadFilters.filters.disqualifiedDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
        },
      })
      navigate('/leads')
      handleClose()
    },
    [
      funnelFilters,
      handleClose,
      navigate,
      saveLeadFilters,
      salesFunnelId,
      salesFunnelName,
    ],
  )

  const goToOpps = useCallback(
    (isClosed: Nullable) => {
      const soldServiceStatuses = data ?? []
      saveOpportunityFilters({
        ...defaultOppFilters,
        filters: {
          ...defaultOppFilters.filters,

          closed: {
            ...defaultOppFilters.filters.closed,
            value: isClosed,
          },
          salesFunnelId: {
            ...defaultOppFilters.filters.salesFunnelId,
            value:
              salesFunnelId && salesFunnelName
                ? {
                    mode: 'any',
                    options: [{ label: salesFunnelName, value: salesFunnelId }],
                  }
                : defaultOppFilters.filters.salesFunnelId.value,
          },
          createdDate:
            isClosed === 'notnull'
              ? defaultOppFilters.filters.createdDate
              : {
                  ...defaultOppFilters.filters.createdDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
          assignee: {
            ...defaultOppFilters.filters.assignee,
            value: funnelFilters.filters.ownerIds.value,
          },
          salesTeamIds: {
            ...defaultOppFilters.filters.salesTeamId,
            value: funnelFilters.filters.salesTeamIds.value,
          },
          soldServiceStatus:
            isClosed === 'null'
              ? defaultOppFilters.filters.soldServiceStatus
              : {
                  ...defaultOppFilters.filters.soldServiceStatus,
                  value: {
                    mode: 'none',
                    options: soldServiceStatuses,
                  },
                },
          closedDate:
            isClosed === 'null'
              ? defaultOppFilters.filters.closedDate
              : {
                  ...defaultOppFilters.filters.closedDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
        },
      })

      navigate('/opportunities')
      handleClose()
    },
    [
      funnelFilters,
      handleClose,
      navigate,
      saveOpportunityFilters,
      salesFunnelId,
      salesFunnelName,
    ],
  )

  const goToLostOpps = useCallback(() => {
    saveOpportunityFilters(buildLostOpportunityFilters(funnelFilters, funnel))

    navigate('/opportunities')
    handleClose()
  }, [funnel, funnelFilters, handleClose, navigate, saveOpportunityFilters])

  return (
    <Box>
      <Grid container>
        <Grid item xs>
          <SalesFunnelMetric
            count={funnel.openLeadsCount}
            handleClick={() => goToLeads('null')}
            title="Open Leads"
            value={funnel.openLeadsValue}
            variant="h4"
          />
        </Grid>
        <Grid item xs>
          <SalesFunnelMetric
            count={funnel.openOpportunitiesCount}
            handleClick={() => goToOpps('null')}
            title="Open Opportunities"
            value={funnel.openOpportunitiesValue}
            variant="h4"
          />
        </Grid>
        <Grid item xs>
          <SalesFunnelMetric
            handleClick={() => goToLeads('notnull')}
            actionBtn={
              <ReasonsPopover
                count={funnel.disqualifiedLeadsCount}
                reasons={funnel.disqualifiedReasons}
                status="disqualified"
              />
            }
            count={funnel.disqualifiedLeadsCount}
            subtitle="Top 3 Disqualified Reasons"
            title="Disqualified"
            value={funnel.disqualifiedLeadsValue}
            variant="h4"
          />
        </Grid>
        <Grid item xs>
          <SalesFunnelMetric
            handleClick={goToLostOpps}
            actionBtn={
              <ReasonsPopover
                count={funnel.lostOpportunitiesCount}
                reasons={funnel.lostReasons}
                status="lost"
              />
            }
            count={funnel.lostOpportunitiesCount}
            subtitle="Top 3 Lost Reasons"
            title="Lost"
            value={funnel.lostOpportunitiesValue}
            variant="h4"
          />
        </Grid>
      </Grid>
    </Box>
  )
}

SalesFunnelCardMetrics.displayName = 'SalesFunnelCardMetrics'
